import { AfterViewInit, Component, Inject, inject, OnInit, signal, ViewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { SnackbarService } from '../../../../shared/services/snackbar/snackbar.service';
import { InceptedApeByAdvisorsV2 } from '../../dashboard-interfaces/incepted-ape-by-advisors-v2.interface';
import { ApeCarrierSplitV2 } from '../../dashboard-interfaces/ape-carrier-split-v2.interface';
import { DashboardManagerTeamServiceV2 } from '../../dashboard-services/dashboard-manager-team/dashboard-manager-team-v2.service';
import { ApeCarrierSplitServiceV2 } from '../../dashboard-services/ape-carrier-split/ape-carrier-split-v2.service';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { HttpStatusCode } from 'axios';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';

@Component({
  selector: 'app-view-incepted-ape-manager-advisors-v2',
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    ReactiveFormsModule,
    MatDividerModule
  ],
  templateUrl: './view-incepted-ape-manager-advisors-v2.dialog.html',
  styleUrl: './view-incepted-ape-manager-advisors-v2.dialog.scss'
})
export class ViewInceptedApeManagerAdvisorsDialogV2 implements OnInit, AfterViewInit {

  private router = inject(Router);
  private snackBarService = inject(SnackbarService);
  private dashboardManagerTeamService = inject(DashboardManagerTeamServiceV2);
  // Same carrier colours, monograms and MTD/YTD split the Incepted APE tables use.
  private readonly apeCarrierSplitService = inject(ApeCarrierSplitServiceV2);

  private inceptedApeByAdvisorData: InceptedApeByAdvisorsV2[] = [];
  // Carrier-chip layout (design 1c), matching the Incepted APE tables: one MTD
  // and one YTD cell holding the combined total, expandable into a mix bar + a
  // chip per carrier.
  protected inceptedApeByAdvisorTableDisplayDataRow: string[] = ["repCode", "personName", "mtdByCarrier", "ytdByCarrier"/*, "yearTotal"*/];
  protected inceptedApeByAdvisorTableDataSource = new MatTableDataSource<InceptedApeByAdvisorsV2>();

  /** Rows whose carrier chips are open, keyed by rep code so paging and sorting keep them open. */
  private readonly expandedRows = new Set<string>();

  protected isRowExpanded(repCode: string): boolean {
    return this.expandedRows.has(repCode);
  }

  protected toggleRow(repCode: string): void {
    if (this.expandedRows.has(repCode)) {
      this.expandedRows.delete(repCode);
    } else {
      this.expandedRows.add(repCode);
    }
  }

  /** Carriers to render for a row, tolerant of rows the split never reached. */
  protected carriersOf(row: InceptedApeByAdvisorsV2): ApeCarrierSplitV2[] {
    return this.apeCarrierSplitService.carriersOf(row);
  }

  inceptedApeBranchAdvisorTableFilterFc = new FormControl("");

  @ViewChild('inceptedApeByAdvisorTablePaginator')
  inceptedApeByAdvisorTablePaginator!: MatPaginator;
  @ViewChild('inceptedApeByAdvisorTableSort')
  inceptedApeByAdvisorTableSort = new MatSort();

  protected managerRepCode: string = "";
  protected managerName: string = "";
  protected targetYear: string = "";

  protected setSprinner = signal(false);

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public matData: any
  ) { }

  async ngOnInit(): Promise<void> {
    this.managerRepCode = this.matData.managerRepCode;
    this.managerName = this.matData.managerName;
    this.targetYear = this.matData.targetYear;

    await this.getDashboardDataByBranchAdvisors(this.managerRepCode, this.targetYear);

    this.inceptedApeByAdvisorTableDataSource.data = this.inceptedApeByAdvisorData;
  }

  ngAfterViewInit(): void {
    this.inceptedApeByAdvisorTableDataSource.paginator = this.inceptedApeByAdvisorTablePaginator;
    this.inceptedApeByAdvisorTableDataSource.sort = this.inceptedApeByAdvisorTableSort;
  }

  private async getDashboardDataByBranchAdvisors(managerRepCode: string, targetYear: string) {
    this.setSprinner.set(true);

    const responseData = await this.dashboardManagerTeamService
      .getAgentsByPiasId(managerRepCode, Number.parseInt(targetYear))
      .then((response) => {
        return JSON.parse(response);
      })
      .catch((error) => {
        // console.error(error);
        return error;
      });

    this.setSprinner.set(false);

    if (responseData.status === HttpStatusCode.Ok) {
      const advisorData: any[] = responseData.data.responsedata;
      advisorData.forEach((advisorItem) => {
        this.inceptedApeByAdvisorData.push({
          repCode: advisorItem.piasRepCode.toString(),
          personName: advisorItem.agentRealName.toString() || advisorItem.agentPreferredName.toString(),
          branch: advisorItem.branch.toString(),
          monthSinglife: Number.parseInt(advisorItem.mtdSinglife.toString()),
          monthSinglifeCase: Number.parseInt(advisorItem.mtdSinglifeCase.toString()),
          monthNonsinglife: Number.parseInt(advisorItem.mtdNonSinglife.toString()),
          monthNonsinglifeCase: Number.parseInt(advisorItem.mtdNonSinglifeCase.toString()),
          yearSinglife: Number.parseInt(advisorItem.ytdSinglife.toString()),
          yearSinglifeCase: Number.parseInt(advisorItem.ytdSinglifeCase.toString()),
          yearNonsinglife: Number.parseInt(advisorItem.ytdNonSinglife.toString()),
          yearNonsinglifeCase: Number.parseInt(advisorItem.ytdNonSinglifeCase.toString()),
          monthChinalife: Number.parseInt(advisorItem.mtdChinalife.toString()),
          monthChinalifeCase: Number.parseInt(advisorItem.mtdChinalifeCase.toString()),
          yearChinalife: Number.parseInt(advisorItem.ytdChinalife.toString()),
          yearChinalifeCase: Number.parseInt(advisorItem.ytdChinalifeCase.toString()),
          monthChinataiping: Number.parseInt(advisorItem.mtdChinataiping.toString()),
          monthChinataipingCase: Number.parseInt(advisorItem.mtdChinataipingCase.toString()),
          yearChinataiping: Number.parseInt(advisorItem.ytdChinataiping.toString()),
          yearChinataipingCase: Number.parseInt(advisorItem.ytdChinataipingCase.toString()),
          monthEtiqa: Number.parseInt(advisorItem.mtdEtiqa.toString()),
          monthEtiqaCase: Number.parseInt(advisorItem.mtdEtiqaCase.toString()),
          yearEtiqa: Number.parseInt(advisorItem.ytdEtiqa.toString()),
          yearEtiqaCase: Number.parseInt(advisorItem.ytdEtiqaCase.toString()),
          monthFwd: Number.parseInt(advisorItem.mtdFwd.toString()),
          monthFwdCase: Number.parseInt(advisorItem.mtdFwdCase.toString()),
          yearFwd: Number.parseInt(advisorItem.ytdFwd.toString()),
          yearFwdCase: Number.parseInt(advisorItem.ytdFwdCase.toString()),
          monthIncome: Number.parseInt(advisorItem.mtdIncome.toString()),
          monthIncomeCase: Number.parseInt(advisorItem.mtdIncomeCase.toString()),
          yearIncome: Number.parseInt(advisorItem.ytdIncome.toString()),
          yearIncomeCase: Number.parseInt(advisorItem.ytdIncomeCase.toString()),
          monthManulife: Number.parseInt(advisorItem.mtdManulife.toString()),
          monthManulifeCase: Number.parseInt(advisorItem.mtdManulifeCase.toString()),
          yearManulife: Number.parseInt(advisorItem.ytdManulife.toString()),
          yearManulifeCase: Number.parseInt(advisorItem.ytdManulifeCase.toString()),
          monthRhi: Number.parseInt(advisorItem.mtdRhi.toString()),
          monthRhiCase: Number.parseInt(advisorItem.mtdRhiCase.toString()),
          yearRhi: Number.parseInt(advisorItem.ytdRhi.toString()),
          yearRhiCase: Number.parseInt(advisorItem.ytdRhiCase.toString()),
          monthTokiomarine: Number.parseInt(advisorItem.mtdTokiomarine.toString()),
          monthTokiomarineCase: Number.parseInt(advisorItem.mtdTokiomarineCase.toString()),
          yearTokiomarine: Number.parseInt(advisorItem.ytdTokiomarine.toString()),
          yearTokiomarineCase: Number.parseInt(advisorItem.ytdTokiomarineCase.toString()),
          monthTransamerica: Number.parseInt(advisorItem.mtdTransamerica.toString()),
          monthTransamericaCase: Number.parseInt(advisorItem.mtdTransamericaCase.toString()),
          yearTransamerica: Number.parseInt(advisorItem.ytdTransamerica.toString()),
          yearTransamericaCase: Number.parseInt(advisorItem.ytdTransamericaCase.toString()),
          monthIfast: Number.parseInt(advisorItem.mtdIfast.toString()),
          monthIfastCase: Number.parseInt(advisorItem.mtdIfastCase.toString()),
          yearIfast: Number.parseInt(advisorItem.ytdIfast.toString()),
          yearIfastCase: Number.parseInt(advisorItem.ytdIfastCase.toString()),
          monthGrow: Number.parseInt(advisorItem.mtdGrow.toString()),
          monthGrowCase: Number.parseInt(advisorItem.mtdGrowCase.toString()),
          yearGrow: Number.parseInt(advisorItem.ytdGrow.toString()),
          yearGrowCase: Number.parseInt(advisorItem.ytdGrowCase.toString()),
          monthTiger: Number.parseInt(advisorItem.mtdTiger.toString()),
          monthTigerCase: Number.parseInt(advisorItem.mtdTigerCase.toString()),
          yearTiger: Number.parseInt(advisorItem.ytdTiger.toString()),
          yearTigerCase: Number.parseInt(advisorItem.ytdTigerCase.toString()),
          monthFwdEmber: Number.parseInt(advisorItem.mtdFwdember.toString()),
          monthFwdEmberCase: Number.parseInt(advisorItem.mtdFwdemberCase.toString()),
          yearFwdEmber: Number.parseInt(advisorItem.ytdFwdember.toString()),
          yearFwdEmberCase: Number.parseInt(advisorItem.ytdFwdemberCase.toString()),
          monthHsbc: Number.parseInt(advisorItem.mtdHsbc.toString()),
          monthHsbcCase: Number.parseInt(advisorItem.mtdHsbcCase.toString()),
          yearHsbc: Number.parseInt(advisorItem.ytdHsbc.toString()),
          yearHsbcCase: Number.parseInt(advisorItem.ytdHsbcCase.toString()),
          yearTarget: 0,
          yearTotal: (Number.parseInt(advisorItem.ytdSinglife.toString()) + Number.parseInt(advisorItem.ytdNonSinglife.toString())),
          userGroup: advisorItem.userGroup.toString()
        });
      });

      // this.inceptedApeByAdvisorData = this.inceptedApeByAdvisorData.filter(item => item.userGroup !== 'Affinity');

      // Fills in each row's MTD/YTD totals and per-carrier split for the chips.
      this.apeCarrierSplitService.applyCarrierSplit(this.inceptedApeByAdvisorData);
    } else {
      if (responseData.code === HttpStatusCode.BadRequest) {
        // Display notification on API failure.
        this.snackBarService.openFailureSnackBar("We couldn’t process your request. Please try again");
      }
      if (responseData.code === HttpStatusCode.Unauthorized) {
        this.snackBarService.openFailureSnackBar("Session timed out. Try logging in again!");
        this.router.navigate(['/login']);
      }
    }
  }

  protected clearSearchField() {
    this.inceptedApeBranchAdvisorTableFilterFc.setValue('');
    this.__applyInceptedApeBranchAdvisorsDataFilter();
  }

  protected __applyInceptedApeBranchAdvisorsDataFilter() {
    let filterValue: string = this.inceptedApeBranchAdvisorTableFilterFc.value?.toString() || '';

    let tempInceptedApeByAdvisorTableData: InceptedApeByAdvisorsV2[] = [];

    if (filterValue) {
      tempInceptedApeByAdvisorTableData = this.inceptedApeByAdvisorData.filter(apeData =>
        apeData.repCode.toString().includes(filterValue)
        || apeData.personName.toLowerCase().includes(filterValue.toLowerCase()
        ));
    } else {
      tempInceptedApeByAdvisorTableData = this.inceptedApeByAdvisorData;
    }

    this.inceptedApeByAdvisorTableDataSource.data = tempInceptedApeByAdvisorTableData;
  }

}
